import { useState, useEffect } from 'react';
import { Message } from '../../types/community';
import { Users, Pin, Trash2, GraduationCap, FileCode2, Bot, Link, FolderOpen, X } from 'lucide-react';
import { formatTimeShort } from '../../utils/formatters';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CommentSection } from './CommentSection';
import { VideoEmbed } from './VideoEmbed';
import { useNavigate } from 'react-router-dom'; 
import { supabase } from '../../lib/supabase';
import { Portal } from '../common/Portal';
import { LiveEventInlineCard, InlineEvent } from './LiveEventInlineCard';

interface ResourceCard {
  type: 'class' | 'blueprint' | 'ai_tool';
  id: string;
  title: string;
  description: string;
  image_url?: string;
}

interface MessageItemProps {
  message: Message;
  isAdmin: boolean;
  currentUserId?: string;
  onTogglePin: (messageId: string, isPinned: boolean) => void;
  onDelete: (messageId: string, userId: string) => void;
  onAddComment: (messageId: string, content: string) => Promise<boolean>;
  onOpenCategoryModal?: (messageId: string) => void;
  onToggleLike?: (messageId: string) => void;
  onViewThread?: (threadId: string) => void;
}

export function MessageItem({
  message,
  isAdmin,
  currentUserId,
  onTogglePin,
  onDelete,
  onAddComment,
  onOpenCategoryModal,
  onToggleLike: _onToggleLike,
  onViewThread,
}: MessageItemProps) {
  const navigate = useNavigate();
  const [resourceCard, setResourceCard] = useState<ResourceCard | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [liveEvent, setLiveEvent] = useState<InlineEvent | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);

  // Compute modal sizing based on image aspect ratio
  const aspect = imgDims ? imgDims.w / imgDims.h : null;
  const modalSizeClass = aspect == null
    ? 'max-w-[85vw] max-h-[80vh]'
    : aspect > 1.2
      ? 'max-w-[92vw] max-h-[70vh]'
      : aspect < 0.8
        ? 'max-w-[65vw] max-h-[90vh]'
        : 'max-w-[80vw] max-h-[80vh]';


  // Function to detect video URLs in message content
  const detectVideoUrl = (content: string) => {
    // YouTube regex patterns
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)/;
    // Loom regex patterns
    const loomRegex = /(?:https?:\/\/)?(?:www\.)?loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)(?:[^\s]*)/;
    
    // Check for YouTube links
    const youtubeMatch = content.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[0]) {
      return youtubeMatch[0];
    }
    
    // Check for Loom links
    const loomMatch = content.match(loomRegex);
    if (loomMatch && loomMatch[0]) {
      return loomMatch[0];
    }
    
    return null;
  };
  
  // Function to remove video URLs from the displayed message content
  const getFilteredContent = (content: string, videoUrl: string | null): string => {
    if (!videoUrl) return content;
    
    // Remove the video URL from the content
    return content.replace(videoUrl, '').trim();
  };



  // Handle category change


  useEffect(() => {
    // Detect video URLs in message content
    const detectedVideoUrl = detectVideoUrl(message.content);
    setVideoUrl(detectedVideoUrl);
    
    // Categories are now handled at Community component level
    
    const fetchClassData = async () => {
      try {
        // Check for resource URLs in the message
        const urlMatch = message.content.match(/(classroom|blueprints|ai-tool)\/([a-zA-Z0-9-]+)/);
        if (!urlMatch) return;

        const resourceType = urlMatch[1];
        const resourceId = urlMatch[2];
        if (!resourceId) return;

        let data, error;
        if (resourceType === 'classroom') {
          ({ data, error } = await supabase
            .from('classes')
            .select('title, description, thumbnail')
            .eq('id', resourceId)
            .single());

          if (error || !data) return;

          setResourceCard({
            type: 'class',
            id: resourceId,
            title: data.title,
            description: data.description,
            image_url: data.thumbnail
          });
        } else if (resourceType === 'blueprints') {
          ({ data, error } = await supabase
            .from('blueprints')
            .select('title, description, logo_url')
            .eq('id', resourceId)
            .single());

          if (error || !data) return;

          setResourceCard({
            type: 'blueprint',
            id: resourceId,
            title: data.title,
            description: data.description,
            image_url: data.logo_url
          });
        } else if (resourceType === 'ai-tool') {
          ({ data, error } = await supabase
            .from('ai_tools')
            .select('title, description, logo_url')
            .eq('id', resourceId)
            .single());

          if (error || !data) return;

          setResourceCard({
            type: 'ai_tool',
            id: resourceId,
            title: data.title,
            description: data.description,
            image_url: data.logo_url
          });
        }
      } catch (err) {
        console.error('Error fetching resource:', err);
      }
    };

    fetchClassData();
    // Detect event attachment like [event:uuid]
    const match = message.content.match(/\[event:([a-f0-9-]{6,})\]/i);
    const fetchEvent = async (id: string) => {
      try {
        const { data } = await supabase
          .from('live_events')
          .select('id, title, description, event_date, event_end_date, thumbnail_url, event_url, is_started, is_ended, replay_available')
          .eq('id', id)
          .maybeSingle();
        if (data) setLiveEvent(data as InlineEvent);
        else setLiveEvent(null);
      } catch (e) {
        console.error('Failed to fetch inline event', e);
        setLiveEvent(null);
      }
    };
    if (match?.[1]) fetchEvent(match[1]); else setLiveEvent(null);
  }, [message.content]);

  // Close image modal with ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsImageModalOpen(false);
    };
    if (isImageModalOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isImageModalOpen]);

  // No need for click outside handler with modal approach

  // Commented out threading-related code
  // const hasReplies = message.reply_count && message.reply_count > 0;
  
  return (
    <div 
      className="bg-[#151D29]/50 backdrop-blur-md border border-[#c9fffc]/10 rounded-xl p-4 text-white max-w-full overflow-hidden shadow-lg hover:shadow-[#c9fffc]/5 transition-all duration-300 group relative z-30 cursor-pointer" 
      id={`message-${message.id}`}
      onClick={() => onViewThread ? onViewThread(message.id) : navigate(`/community/message/${message.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          onViewThread ? onViewThread(message.id) : navigate(`/community/message/${message.id}`);
        }
      }}
    >
      {/* Subtle glow overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#c9fffc]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl"></div>
      {/* Top right corner neon accent */}
      <div className="absolute -top-8 -right-8 w-16 h-16 bg-[#c9fffc]/5 rounded-full blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-500 pointer-events-none"></div>
      <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex-shrink-0">
            {message.profiles?.avatar_url ? (
              <img
                src={message.profiles.avatar_url}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                <Users className="w-6 h-6" />
              </div>
            )}
          </div>
          <div className="overflow-hidden">
            <span className="text-[#c9fffc] font-medium truncate block">
              {message.profiles?.display_name || message.profiles?.email}
            </span>
            {message.profiles?.is_admin && (
              <span className="text-xs text-[#c9fffc]/60">Admin</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 text-sm whitespace-nowrap">
            {formatTimeShort(message.created_at)}
          </span>
          <div className="flex items-center gap-1.5 relative">
            {/* Permalink button */}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const permalinkUrl = `${window.location.origin}/community/message/${message.id}`;
                try {
                  await navigator.clipboard.writeText(permalinkUrl);
                  // Simple toast using DOM manipulation
                  const toast = document.createElement('div');
                  toast.className = 'fixed top-4 right-4 z-50 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-2 rounded-lg backdrop-blur-md shadow-lg text-sm font-medium';
                  toast.textContent = 'Message link copied!';
                  document.body.appendChild(toast);
                  setTimeout(() => {
                    toast.remove();
                  }, 2000);
                } catch (err) {
                  console.error('Failed to copy link:', err);
                }
              }}
              className="p-1.5 rounded-lg backdrop-blur-sm transition-all duration-300 bg-[#151D29]/50 text-gray-400 hover:text-[#c9fffc] hover:bg-[#c9fffc]/10"
              title="Copy message link"
            >
              <Link className="w-4 h-4" />
            </button>

            {/* Pin/Unpin button (admin only) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isAdmin) {
                  console.log('Pin button clicked');
                  console.log('Message details:', { 
                    id: message.id, 
                    is_pinned: message.is_pinned,
                    isAdmin: isAdmin,
                    user: message.profiles?.email
                  });
                  // Passing the CURRENT pin state, not the negated state
                  onTogglePin(message.id, message.is_pinned);
                }
              }}
              className={`p-1.5 rounded-lg backdrop-blur-sm transition-all duration-300 ${isAdmin ? 'visible' : 'hidden'} ${
                message.is_pinned
                  ? 'bg-[#c9fffc]/10 text-[#c9fffc]' 
                  : 'bg-[#151D29]/50 text-gray-400 hover:text-[#c9fffc] hover:bg-[#c9fffc]/10'
              }`}
              title={message.is_pinned ? 'Unpin message' : 'Pin message'}
            >
              <Pin className="w-4 h-4" />
            </button>

            {/* Category change button (admin only) */}
            <div className={`${isAdmin ? 'visible' : 'hidden'}`}>
              <button
                onClick={(e) => { e.stopPropagation(); onOpenCategoryModal?.(message.id); }}
                className="p-1.5 rounded-lg backdrop-blur-sm transition-all duration-300 bg-[#151D29]/50 text-gray-400 hover:text-[#c9fffc] hover:bg-[#c9fffc]/10"
                title="Move to category"
              >
                <FolderOpen className="w-4 h-4" />
              </button>
            </div>
            
            {/* Delete button (admin or owner) */}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(message.id, message.user_id); }}
              className={`p-1.5 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                isAdmin || message.user_id === currentUserId 
                  ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' 
                  : 'hidden'
              }`}
              title="Delete message"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-full overflow-hidden relative z-10">
        <div className="prose prose-invert prose-sm prose-p:text-white/90 prose-a:text-[#c9fffc] prose-code:text-[#c9fffc] prose-code:bg-[#c9fffc]/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded overflow-hidden">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]} 
            components={{
              // Override paragraph rendering for better mobile display
              p: ({node, ...props}) => <p style={{wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%'}} {...props}/>,
              // Force links to break properly
              a: ({node, ...props}) => <a onClick={(e) => e.stopPropagation()} style={{wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%', display: 'inline-block'}} {...props}/>
            }}
          >
            {/* Use filtered content to hide video URLs and thread metadata */}
            {getFilteredContent(message.content, videoUrl)
              ?.replace(/\[reply:[^\]]+\]/, '')
              ?.replace(/\[event:([a-f0-9-]{6,})\]/i, '')}
          </ReactMarkdown>
        </div>
      </div>
      
      {resourceCard && (
        <div 
          className="mt-4 bg-gray-750 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-700 transition-colors group w-full max-w-xs"
          onClick={(e) => { e.stopPropagation(); navigate(`/${resourceCard.type === 'class' ? 'classroom' : resourceCard.type === 'blueprint' ? 'blueprints' : 'ai-tool'}/${resourceCard.id}`); }}
        >
          <div className="flex items-start gap-3 p-3">
            <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
              {resourceCard.image_url ? (
                <img 
                  src={resourceCard.image_url} 
                  alt={resourceCard.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                {
                  class: <GraduationCap className="w-5 h-5 text-[#c9fffc]" />,
                  blueprint: <FileCode2 className="w-5 h-5 text-[#c9fffc]" />,
                  ai_tool: <Bot className="w-5 h-5 text-[#c9fffc]" />
                }[resourceCard.type]
              )}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <h3 className="text-white font-medium mb-1 truncate text-sm">{resourceCard.title}</h3>
              <p className="text-gray-400 text-xs line-clamp-2 break-words">{resourceCard.description}</p>
            </div>
          </div>
        </div>
      )}
      
      {liveEvent && (
        <LiveEventInlineCard event={liveEvent} />
      )}

      {message.image_url && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIsImageModalOpen(true); }}
            className="mt-2 relative inline-block rounded-lg overflow-hidden border border-[#c9fffc]/10 group"
            title="View image"
          >
            <img 
              src={message.image_url} 
              alt="Message attachment" 
              className="w-28 h-28 object-cover"
            />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          {isImageModalOpen && (
            <Portal>
              <div
                className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                onClick={() => setIsImageModalOpen(false)}
              >
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <img
                    src={message.image_url}
                    alt="Message attachment full"
                    className={`object-contain rounded-lg border border-white/10 ${modalSizeClass}`}
                    onLoad={(e) => setImgDims({ w: (e.target as HTMLImageElement).naturalWidth, h: (e.target as HTMLImageElement).naturalHeight })}
                  />
                  <button
                    onClick={() => setIsImageModalOpen(false)}
                    className="absolute -top-3 -right-3 p-1.5 rounded-full bg-black/70 text-white hover:bg-black/90 border border-white/10"
                    aria-label="Close image"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Portal>
          )}
        </>
      )}
      
      {/* Render embedded video player if a video URL is detected */}
      {videoUrl && <VideoEmbed url={videoUrl} />}
    
      
    
      
      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <CommentSection
          messageId={message.id}
          comments={message.comments || []}
          onAddComment={onAddComment}
          onOpenThread={() => onViewThread?.(message.id)}
        />
      </div>


    </div>
  );
}